<template>
  <!-- 入库资料 연속 업로드(2026-10-18 杨英文 #56) — 「Coupang入库资料」 창의 QR 을 폰으로 찍으면 열린다. 로그인 없이 QR 안의 서명(12시간)으로만 동작.
       ① 박스 라벨 바코드(箱号)를 카메라로 스캔(또는 직접 입력) → ② 입고자료 사진 1장 → 자동 판독(入库ID·예정일·센터) → ③ 다음 박스 … → 마지막에 «完成» -->
  <div class="bps">
    <div class="bps__title">📦 入库资料 连续上传<small>(입고자료 연속 업로드)</small></div>

    <div v-if="finished" class="bps__done">
      <div class="bps__done-title">✅ 全部完成<small>(전부 완료)</small></div>
      <div class="bps__list">
        <div v-for="b in done" :key="b.labelId" class="bps__row">📦 {{ b.labelId }}
          <small v-if="b.read && (b.read.id || b.read.date || b.read.center)"> · {{ b.read.id }} {{ b.read.date }} {{ b.read.center }}</small>
          <small v-else> · 未识别(미판독)</small>
        </div>
      </div>
      <button type="button" class="bps__btn" @click="restart">继续上传<small>(다시 시작)</small></button>
    </div>

    <!-- 앱 안 카메라로 바로 찍기 — 안내앱(딩톡 등) 브라우저는 파일 선택창 카메라(capture)를 막는 일이 있다(2026-10-21 杨英文 #119). 플래시는 켜지 않는다 -->
    <div v-else-if="shootMode" class="bps__shoot">
      <div class="bps__step">{{ shootMode === 'num' ? '拍箱号照片' : '② 拍摄入库资料' }}<small>({{ shootMode === 'num' ? '번호 사진' : '입고자료 사진' }})</small></div>
      <div class="bps__cam"><video ref="shootVideo" playsinline muted class="is-on"></video></div>
      <button type="button" class="bps__btn bps__btn--main" :disabled="!shootReady" @click="snap">📸 拍照<small>(찍기)</small></button>
      <button type="button" class="bps__btn" style="width:100%;margin-top:8px" @click="closeShoot">取消<small>(취소)</small></button>
    </div>

    <template v-else>
      <!-- ① 박스 고르기 -->
      <div v-if="!box">
        <div class="bps__step">① 扫描箱子标签上的条码<small>(박스 라벨 바코드 스캔)</small></div>
        <div class="bps__cam">
          <video ref="video" playsinline muted :class="{ 'is-on': camOn }"></video>
          <canvas ref="canvas" style="display:none"></canvas>
          <div v-if="!camOn" class="bps__cam-off">{{ camError || '相机未打开(카메라 꺼짐)' }}</div>
        </div>
        <label class="bps__btn bps__btn--main bps__lbl" :class="{ 'bps__lbl--off': busy || looking }" style="margin-bottom:8px">📸 拍照识别箱号<small>(사진 찍어 번호 인식)</small>
          <input type="file" accept="image/*" class="bps__file" :disabled="!!busy || looking" @change="onScanPhoto">
        </label>
        <button v-if="!camOn" type="button" class="bps__btn bps__btn--main" @click="startCam">📷 打开相机<small>(카메라 켜기)</small></button>
        <div class="bps__manual">
          <input v-model.trim="manual" type="text" placeholder="扫不出来请输入箱号 (박스번호 직접 입력)" @keyup.enter="lookup(manual)">
          <button type="button" class="bps__btn" :disabled="!manual || looking" @click="lookup(manual)">确定<small>(확인)</small></button>
        </div>
        <!-- 번호를 못 읽을 때 — 箱号(번호 라벨) 사진을 먼저 찍어 올리면 임시번호로 한 묶음이 된다(2026-10-20 杨英文 #119) -->
        <label v-if="inApp" class="bps__btn bps__lbl" style="width:100%" :class="{ 'bps__lbl--off': busy || looking }">📷 扫不出来？拍箱号照片<small>(번호 사진으로 올리기)</small>
          <input type="file" accept="image/*" class="bps__file" :disabled="!!busy || looking" @change="onNumberPhoto">
        </label>
        <button v-else type="button" class="bps__btn" style="width:100%" :disabled="!!busy || looking" @click="openShoot('num')">📷 扫不出来？拍箱号照片<small>(번호 사진으로 올리기)</small></button>
        <input ref="numcam" type="file" accept="image/*" style="display:none" @change="onNumberPhoto">
        <div v-if="busy" class="bps__msg">{{ busy }}</div>
        <div v-if="message" class="bps__msg bps__msg--err">{{ message }}</div>
      </div>

      <!-- ② 입고자료 촬영 -->
      <div v-else>
        <div class="bps__head">
          <div class="bps__no">📦 {{ box.labelId }}</div>
          <div class="bps__meta">{{ box.화주 }}<span v-if="box.박스크기"> · {{ box.박스크기 }}</span>
            <span v-if="box.미등록">未登记的箱号，照常上传即可<small>(시스템에 없는 번호 — 그대로 올리면 됩니다)</small></span></div>
        </div>
        <div class="bps__step">② 拍摄入库资料(1张)<small>(입고자료 사진 1장)</small></div>
        <div class="bps__actions">
          <label v-if="inApp" class="bps__btn bps__btn--main bps__lbl" :class="{ 'bps__lbl--off': busy }">📷 拍照<small>(촬영)</small>
            <input type="file" accept="image/*" class="bps__file" :disabled="!!busy" @change="onFile">
          </label>
          <button v-else type="button" class="bps__btn bps__btn--main" :disabled="busy" @click="openShoot('doc')">📷 拍照<small>(촬영)</small></button>
          <label class="bps__btn bps__lbl" :class="{ 'bps__lbl--off': busy }">🖼 相册<small>(앨범)</small>
            <input type="file" accept="image/*" class="bps__file" :disabled="!!busy" @change="onFile">
          </label>
          <input ref="cam" type="file" accept="image/*" style="display:none" @change="onFile">
        </div>
        <div v-if="busy" class="bps__msg">{{ busy }}</div>
        <div v-if="message" class="bps__msg" :class="{ 'bps__msg--err': messageErr }">{{ message }}</div>
        <img v-if="photo" :src="photo" alt="" class="bps__photo">
        <div v-if="readInfo" class="bps__msg" :class="{ 'bps__msg--err': !readInfo.ok }">
          <template v-if="readInfo.ok">🔎 识别结果<small>(판독)</small> — 入库ID {{ readInfo.id || '-' }} · 入库预定日 {{ readInfo.date || '-' }} · 入库中心 {{ readInfo.center || '-' }}</template>
          <template v-else>未能自动识别，请在电脑上手动填写<small>(자동 판독 실패 — PC 에서 직접 입력)</small></template>
        </div>
        <button type="button" class="bps__btn bps__btn--next" :disabled="!!busy" @click="nextBox">➡ 下一个箱子<small>(다음 박스)</small></button>
      </div>

      <div v-if="done.length" class="bps__sum">
        <div class="bps__sum-title">本次已上传<small>(이번에 올린 박스)</small> {{ done.length }}</div>
        <div v-for="b in done" :key="b.labelId" class="bps__row">✅ {{ b.labelId }}</div>
        <button type="button" class="bps__btn bps__btn--finish" :disabled="!!busy" @click="finishAll">🏁 完成<small>(완료)</small></button>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import FastScanner, { SCAN_FORMATS } from '@/utils/fastScanner';
import { uploadImportPhoto } from '@/utils/importPhotoS3';

export default {
  name: 'BoxPhotoSession',
  data() {
    return {
      box: null, manual: '', looking: false, message: '', messageErr: false, busy: '',
      photo: '', readInfo: null, done: [], finished: false,
      camOn: false, camError: '', stream: null,
      shootMode: '', shootReady: false, shootStream: null
    };
  },
  computed: {
    // 딩톡·微信 앱 안 브라우저 또는 카메라 API 없음 — 이때는 숨긴 입력 click() 대신 라벨 안 입력창을 직접 누르게 한다
    inApp() {
      return /DingTalk|MicroMessenger|MiniProgram/i.test(navigator.userAgent || '') || !(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    },
    token() { return String(this.$route.query.t || ''); }
  },
  mounted() {
    this.scanner = new FastScanner({ tag: '[入库资料]', dupMs: 2500, quaggaMaxW: 1920 });
    this.startCam();
  },
  beforeDestroy() {
    this.stopCam();
    this.stopShoot();
  },
  methods: {
    withTimeout(p, ms) {
      return Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('upload timeout')), ms))]);
    },
    // 앱 안 카메라 열기 — 안 열리면(권한·미지원) 예전 파일 선택창(capture)으로 넘긴다
    async openShoot(mode) {
      if (this.busy) return;
      const fallback = () => { const r = this.$refs[mode === 'num' ? 'numcam' : 'cam']; if (r) r.click(); };
      // 딩톡·微信 앱 안 브라우저는 카메라 직접 열기·capture 입력을 막거나 비동기로 거절한다(«功能未开通») — 바로 사진 선택창(촬영 항목 포함)을 연다. await 뒤의 click 은 iOS 가 무시해서 여기서 곧장
      if (/DingTalk|MicroMessenger|MiniProgram/i.test(navigator.userAgent || '') || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return fallback();
      this.stopCam();
      try {
        // 권한창이 안 떠 영원히 멈추는 환경이 있어 4초까지만 기다린다
        this.shootStream = await Promise.race([
          navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false
          }),
          new Promise((_, rej) => setTimeout(() => rej(new Error('camera timeout')), 4000))
        ]);
        this.shootMode = mode;
        this.shootReady = false;
        await this.$nextTick();
        const v = this.$refs.shootVideo;
        if (!v) throw new Error('video');
        v.srcObject = this.shootStream;
        await v.play();
        // 플래시(토치)는 끈다 — 지원하는 기기만 적용된다
        const tr = this.shootStream.getVideoTracks()[0];
        try { await tr.applyConstraints({ advanced: [{ torch: false }] }); } catch (e) { /* 토치 미지원 */ }
        this.shootReady = true;
      } catch (e) {
        console.error('[入库资料] 촬영 카메라 시작 실패:', e);
        this.stopShoot();
        this.shootMode = '';
        if (!this.box) this.startCam();
        fallback();
      }
    },
    stopShoot() {
      if (this.shootStream) this.shootStream.getTracks().forEach(t => { try { t.stop(); } catch (e) { console.error('[入库资料] 촬영 카메라 정지 실패', e); } });
      this.shootStream = null;
      this.shootReady = false;
    },
    closeShoot() {
      this.stopShoot();
      this.shootMode = '';
      if (!this.box) this.startCam();
    },
    // 셔터 — 보이는 프레임을 바로 사진으로 만든다(대기 없음)
    snap() {
      const v = this.$refs.shootVideo;
      if (!v || !v.videoWidth) return;
      const c = document.createElement('canvas');
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext('2d').drawImage(v, 0, 0);
      const mode = this.shootMode;
      c.toBlob(blob => {
        if (!blob) { this.say('拍照失败，请重试 (촬영 실패)', true); return; }
        const f = new File([blob], 'box-' + Date.now() + '.jpg', { type: 'image/jpeg' });
        this.stopShoot();
        this.shootMode = '';
        const ev = { target: { files: [f], value: '' } };
        if (mode === 'num') this.onNumberPhoto(ev); else this.onFile(ev);
      }, 'image/jpeg', 0.9);
    },
    say(msg, err = false) { this.message = msg; this.messageErr = err; },
    async startCam() {
      this.camError = '';
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.camError = '此浏览器不支持相机，请手动输入箱号(카메라 미지원 — 직접 입력)';
        return;
      }
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false
        });
        const v = this.$refs.video;
        if (!v) return;
        v.srcObject = this.stream;
        await v.play();
        this.camOn = true;
        await this.scanner.start({
          video: v,
          canvas: this.$refs.canvas,
          enabled: () => !this.box && !this.looking,
          formats: () => SCAN_FORMATS.LABEL,
          onDetect: val => this.lookup(val, true)
        });
      } catch (e) {
        console.error('[入库资料] 카메라 시작 실패:', e);
        this.camError = '相机打开失败，请手动输入箱号 (' + e.message + ')';
        this.stopCam();
      }
    },
    stopCam() {
      if (this.scanner) this.scanner.stop();
      if (this.stream) {
        this.stream.getTracks().forEach(t => { try { t.stop(); } catch (e) { console.error('[入库资料] 카메라 정지 실패', e); } });
      }
      this.stream = null;
      this.camOn = false;
    },
    async lookup(code, fromScan) {
      // 라벨 바코드는 줄바꿈(CRLF)까지 담고 있다 — 서버 표준(\n 한 글자)으로 맞춘다
      const id = String(code || '').replace(/\r\n?/g, '\n').trim();
      if (!id || this.looking || this.box) return;
      // 箱号는 영문·숫자·-·_ 뿐이다 — 흐린 화면을 잘못 읽은 값(%·&·@ 등 섞임)은 조회하지 않고 다음 프레임을 다시 읽는다
      // 손으로 친 번호는 그대로 조회한다(오타 안내가 필요하다)
      if (fromScan && !/^[A-Za-z0-9_ \n-]+$/.test(id)) return;
      this.looking = true;
      this.say('');
      try {
        const { data } = await axios.post('/api/boxPhoto/info', { labelId: id, t: this.token });
        if (data.요청결과 === '성공') {
          this.box = data.box;
          this.photo = '';
          this.readInfo = null;
          this.manual = '';
          this.stopCam();
        } else if (data.error && data.error.errorCode === '401') {
          this.say('二维码已失效，请回电脑重新点「扫码上传」(QR 만료 — PC 에서 다시)', true);
        } else {
          this.say('找不到这个箱子: ' + id + ' (박스를 찾을 수 없음)', true);
        }
      } catch (e) {
        console.error('[入库资料] 박스 조회 오류:', e);
        this.say('网络错误，请重试 (네트워크 오류)', true);
      } finally {
        this.looking = false;
      }
    },
    async onFile(e) {
      const f = e.target && e.target.files && e.target.files[0];
      e.target.value = '';
      if (!f || !this.box) return;
      this.say('');
      this.readInfo = null;
      this.photo = URL.createObjectURL(f);
      const auth = { labelId: this.box.labelId, t: this.token };
      try {
        this.busy = '上传中...(업로드 중)';
        const url = await this.withTimeout(uploadImportPhoto(f, 'box-' + this.box.labelId), 60000);
        const add = await axios.post('/api/boxPhoto/add', { ...auth, urls: [url] });
        if (add.data.요청결과 !== '成功' && add.data.요청결과 !== '성공') throw new Error((add.data.error && add.data.error.errorMessage) || '저장 실패');
        await axios.post('/api/boxPhoto/done', auth);
        const labelId = this.box.labelId;
        const rest = this.done.filter(b => b.labelId !== labelId);
        this.done = rest.concat([{ labelId, read: null }]);
        this.say('已保存 ✔ 可继续下一个箱子 (저장됨 — 다음 박스로 넘어가도 됩니다)', false);
        // 입고ID·예정일·센터 판독은 뒤에서 — 끝나면 같은 박스 화면일 때만 결과를 보여 준다. 실패해도 사진 저장에는 영향 없다
        axios.post('/api/boxPhoto/inbound', { ...auth, url }).then(({ data }) => {
          const r = data.요청결과 === '성공' ? data.read : null;
          const info = r && (r.id || r.date || r.center) ? { ok: true, id: r.id, date: r.date, center: r.center } : { ok: false };
          this.done = this.done.map(b => (b.labelId === labelId ? { labelId, read: info.ok ? info : null } : b));
          if (this.box && this.box.labelId === labelId) this.readInfo = info;
        }).catch(err => console.error('[入库资料] 판독 오류(사진은 저장됨):', err));
      } catch (err) {
        console.error('[入库资料] 업로드 오류:', err);
        this.say('上传失败，请重试 (업로드 실패 — 다시 시도): ' + err.message, true);
      } finally {
        this.busy = '';
      }
    },
    // 箱号 사진 — 번호를 못 읽을 때: 번호 라벨 사진을 올려 임시번호(未识别-…)로 박스를 만들고, 이어서 입고자료 사진을 올린다
    // 박스 라벨 사진 한 장에서 바코드를 읽어 조회 — 못 읽으면 안내(직접 입력·번호 사진 업로드로 이어 갈 수 있다)
    async onScanPhoto(e) {
      const f = e.target && e.target.files && e.target.files[0];
      e.target.value = '';
      if (!f || this.box || this.looking) return;
      this.say('');
      this.busy = '🔎 正在识别箱号...(번호 인식 중)';
      let url = '';
      try {
        url = URL.createObjectURL(f);
        const val = await Promise.race([
          this.scanner.decodePhoto(url, SCAN_FORMATS.LABEL),
          new Promise(resolve => setTimeout(() => resolve(''), 40000))
        ]);
        this.busy = '';
        if (!val) { this.say('没识别出来，请把条码拍得更近更清楚后重拍，或手动输入箱号 (인식 실패 — 바코드를 더 가깝고 또렷하게 다시 찍거나 직접 입력)', true); return; }
        await this.lookup(val, true);
      } catch (err) {
        console.error('[入库资料] 사진 번호 인식 오류:', err);
        this.say('识别失败，请手动输入箱号 (인식 오류 — 직접 입력): ' + err.message, true);
      } finally {
        this.busy = '';
        if (url) URL.revokeObjectURL(url);
      }
    },
    async onNumberPhoto(e) {
      const f = e.target && e.target.files && e.target.files[0];
      e.target.value = '';
      if (!f || this.box) return;
      this.say('');
      const d = new Date();
      const pad = n => String(n).padStart(2, '0');
      const id = '未识别-' + pad(d.getMonth() + 1) + pad(d.getDate()) + pad(d.getHours()) + pad(d.getMinutes()) + '-' + Math.random().toString(36).slice(2, 5);
      try {
        this.busy = '上传中...(업로드 중)';
        const url = await this.withTimeout(uploadImportPhoto(f, 'box-unknown'), 60000);
        const { data } = await axios.post('/api/boxPhoto/add', { labelId: id, t: this.token, urls: [url], kind: '箱号' });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '저장 실패');
        this.box = Object.assign({}, data.box, { 미등록: true });
        this.photo = '';
        this.readInfo = null;
        this.stopCam();
        this.say('已记录箱号照片，请继续拍摄入库资料 (번호 사진 저장됨 — 이어서 입고자료 사진)');
      } catch (err) {
        console.error('[入库资料] 箱号 사진 업로드 오류:', err);
        this.say('上传失败，请重试 (업로드 실패 — 다시 시도): ' + err.message, true);
      } finally {
        this.busy = '';
      }
    },
    nextBox() {
      this.box = null;
      this.photo = '';
      this.readInfo = null;
      this.say('');
      this.scanner.resetDup();
      this.startCam();
    },
    finishAll() {
      this.stopCam();
      this.finished = true;
    },
    restart() {
      this.finished = false;
      this.nextBox();
    }
  }
};
</script>

<style scoped>
.bps { max-width: 560px; margin: 0 auto; padding: 12px; font-size: 16px; color: #111; }
.bps small { margin-left: 4px; font-size: 12px; opacity: .7; }
.bps__title { font-size: 20px; font-weight: 700; margin-bottom: 10px; }
.bps__step { margin: 10px 0 6px; font-weight: 700; }
.bps__cam { position: relative; width: 100%; aspect-ratio: 4 / 3; background: #0f172a; border-radius: 10px; overflow: hidden; }
.bps__cam video { width: 100%; height: 100%; object-fit: cover; opacity: 0; }
.bps__cam video.is-on { opacity: 1; }
.bps__cam-off { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; padding: 12px; text-align: center; }
.bps__manual { display: flex; gap: 8px; margin: 10px 0; }
.bps__manual input { flex: 1; border: 1px solid #94a3b8; border-radius: 10px; padding: 12px; font-size: 16px; min-width: 0; }
.bps__btn { border: 1px solid #94a3b8; background: #fff; border-radius: 10px; padding: 14px 10px; font-size: 17px; font-weight: 600; }
.bps__btn--main { background: #2563eb; border-color: #2563eb; color: #fff; width: 100%; margin-top: 8px; }
.bps__btn--next { width: 100%; background: #16a34a; border-color: #16a34a; color: #fff; margin-top: 12px; }
.bps__btn--finish { width: 100%; background: #f59e0b; border-color: #f59e0b; color: #fff; margin-top: 10px; }
.bps__btn:disabled { opacity: .45; }
.bps__lbl { position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; text-align: center; cursor: pointer; box-sizing: border-box; }
.bps__lbl small { margin-left: 4px; }
.bps__file { position: absolute; left: 0; top: 0; width: 100%; height: 100%; opacity: 0; font-size: 100px; cursor: pointer; }
.bps__lbl--off { opacity: .45; pointer-events: none; }
.bps__actions { display: flex; gap: 8px; margin: 8px 0; }
.bps__actions .bps__btn { flex: 1; margin-top: 0; }
.bps__head { background: #f1f5f9; border-radius: 8px; padding: 10px 12px; }
.bps__no { font-size: 20px; font-weight: 700; word-break: break-all; }
.bps__meta { opacity: .75; }
.bps__msg { margin: 8px 0; padding: 8px 10px; border-radius: 8px; background: #ecfdf5; }
.bps__msg--err { background: #fef2f2; color: #b00020; }
.bps__photo { width: 100%; border-radius: 10px; margin-top: 8px; }
.bps__sum { margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
.bps__sum-title { font-weight: 700; margin-bottom: 4px; }
.bps__row { padding: 2px 0; word-break: break-all; }
.bps__done { text-align: center; padding: 24px 0; }
.bps__done-title { font-size: 22px; font-weight: 700; color: #1b7f3b; margin-bottom: 12px; }
.bps__list { text-align: left; margin-bottom: 12px; }
</style>
